/**
 * 전송기 (TASK-2026-004 SPEC 「포함 범위」 4·6, 중앙 명세 F1-05·F1-06, AC-01~04).
 *
 * 계측 로그를 원본으로 보고 아직 서버에 없는 것만 batch로 올린다. 이 모듈이 지키는
 * 성질을 먼저 적는다. 아래 중 하나라도 어기면 조기 종료한 참가자의 자료가 사라지거나
 * 보내지 않은 자료를 보냈다고 표시하게 된다.
 *
 * 1. **자료를 지우는 유일한 근거는 서버 ACK다.** ACK 전에는 로컬 표식을 앞으로 옮기지
 *    않는다. 로그 자체는 참가자의 기록이므로 전송기가 지우지 않는다.
 * 2. **보내던 batch는 같은 바이트로 다시 보낸다.** 계획을 세우면 먼저 저장소에 적고
 *    나서 보낸다. 창이 닫혀도 같은 `batch_id`로 재전송되므로 서버가 중복을 합친다.
 * 3. **쓰는 탭은 하나다.** lease를 못 가진 탭은 아무 요청도 만들지 않는다.
 * 4. **오프라인·실패를 성공으로 바꾸지 않는다.** 상태는 `전송 중 / 일부 미전송 /
 *    저장 완료`로 사실대로 나가고, 완료 표시는 `synced`(서버 ACK가 로컬 마지막 순번에
 *    닿았다)일 때만 허용한다.
 * 5. **재시도는 유계다.** 다시 보내도 같은 답이 오는 실패(4xx)는 반복하지 않고,
 *    통신·서버 오류는 backoff로 간격을 늘린다.
 */

import { chunksOf, toPathChunks, MAX_CHUNK_MOVES, type AttemptState, type PathChunk } from '../app/attempt';
import type { ConfirmationSpool, SpoolSnapshot } from '../telemetry/confirmationSpool';
import type { TelemetryEvent } from '../telemetry/events';
import type { TelemetryIntegrity } from '../telemetry/store';
import { ApiError, type ApiClient, type SessionState } from './client';
import type { Lease } from './lease';
import {
  chunkKey,
  integrityWorse,
  MAX_BATCH_CONFIRMATIONS,
  planBatch,
  type ClientIntegrity,
  type ConfirmationIntegrity,
} from './outbox';
import {
  appendSegment,
  readQueueState,
  writeQueueState,
  type ClockSegment,
  type QueueStateShape,
  type StateStorage,
} from './state';

/** 원격 batch 간격 (중앙 명세 F1-05: 10초 또는 유효 이동 50회). */
export const FLUSH_INTERVAL_MS = 10_000;
export const FLUSH_MOVE_COUNT = 50;
/** 대기 확인 기록이 이만큼 쌓이면 주기를 기다리지 않고 보낸다 (CR-01 A+ §2.2). */
export const FLUSH_CONFIRMATION_COUNT = 100;

/** 재시도 간격. 실패가 이어지면 늘리고 상한에서 멈춘다. */
export const BACKOFF_BASE_MS = 2_000;
export const BACKOFF_MAX_MS = 60_000;

/** 한 번의 `flushAll`에서 보낼 batch 수 상한. 무한 루프를 만들지 않는다. */
export const MAX_BATCHES_PER_FLUSH = 40;

export type SenderState = 'idle' | 'sending' | 'pending' | 'offline' | 'readonly' | 'blocked';

export interface SenderStatus {
  readonly state: SenderState;
  readonly pendingEvents: number;
  readonly pendingChunks: number;
  readonly throughSeq: number;
  readonly localMaxSeq: number;
  readonly conflicts: number;
  /** 아직 서버 ACK를 받지 못한 저장 확인 기록 수 (REV-005). */
  readonly pendingConfirmations: number;
  /** 이 세션의 누적 저장 확인 기록 손실 수 (REV-005, CR-01 A+). spool에서 읽어 페이지를 넘어 이어진다. */
  readonly droppedConfirmations: number;
  /** spool 상태. `unverified`면 정상 완료를 주장하지 않는다 (CR-01 A+ §2.1.7). */
  readonly confirmationsState: SpoolSnapshot['state'];
  readonly confirmationsReason: SpoolSnapshot['reason'];
  readonly lastError: string | null;
  readonly lastAckAt: number | null;
  readonly tries: number;
  readonly holder: boolean;
  /**
   * 서버가 이 세션의 완료를 확인해 봉인했는가 (TASK-2026-012 CR-03).
   *
   * 봉인 뒤에는 완료 경계보다 큰 로컬 기록을 미전송으로 세지도, 보내지도 않는다. 이 전송기는
   * 항상 채운다. 없으면 봉인되지 않은 것으로 읽는다(화면은 `=== true`로만 판정한다).
   */
  readonly sealed?: boolean;
  /**
   * 서버 ACK가 로컬 마지막 순번에 닿았고 보낼 것이 하나도 남지 않았는가.
   *
   * 이벤트·경로 조각뿐 아니라 **저장 확인 기록과 보내던 batch까지** 0이어야 한다
   * (REV-005·REV-006). 완료 표시는 이 값만으로도 부족하다 —— 최종 제출과 서버가
   * 확정한 결과까지 확인한 뒤에만 `저장 완료`다.
   */
  readonly synced: boolean;
}

export type FlushOutcome =
  | { readonly kind: 'sent'; readonly batchId: string; readonly throughSeq: number; readonly duplicate: boolean }
  | { readonly kind: 'idle' }
  | { readonly kind: 'not_holder' }
  | { readonly kind: 'offline' }
  /** 다시 시도할 수 있는 실패. 자료는 그대로 남는다. */
  | { readonly kind: 'retry'; readonly code: string; readonly retryAfterMs: number }
  /** 같은 요청을 반복해도 달라지지 않는 실패. */
  | { readonly kind: 'blocked'; readonly code: string };

export interface FlushOptions {
  /**
   * 참가자가 `지금 다시 보내기`를 눌렀는가 (TASK-2026-012).
   *
   * 참이면 브라우저의 온라인 표시(`navigator.onLine`)가 거짓이어도 실제 요청을 시도한다.
   * 그 표시는 전송 가능성의 힌트일 뿐 서버 ACK가 아니고, 연결이 돌아온 뒤에도 거짓으로
   * 남아 수동 재시도까지 막은 사례가 있다. 임차·차단 사유·세션 확인·CSRF 검사는 그대로
   * 적용되고, 결과는 실제 서버 응답으로만 판정한다. 자동 주기는 계속 힌트를 따른다.
   */
  readonly manual?: boolean;
}

export interface EventSource {
  readAll(): readonly TelemetryEvent[];
  integrity(): TelemetryIntegrity;
}

export interface SenderDeps {
  readonly client: ApiClient;
  readonly log: EventSource;
  /** 진행 중인 시도. 완결된 50수 조각을 시도가 끝나기 전에 보내는 근거다. */
  readonly checkpoint: () => AttemptState | undefined;
  /** 이 세션의 저장 확인 기록 spool (ADR-002). */
  readonly confirmations: ConfirmationSpool;
  readonly storage: StateStorage;
  readonly lease: Lease;
  readonly sessionRef: string;
  /**
   * 서버가 돌려준 세션이 이 전송기의 참여인지 확인하고, 맞으면 쓰기 토큰(CSRF)을
   * 받아들인다 (R2-REV-003). 맞지 않으면 `false`를 돌려주고 아무것도 받아들이지 않는다.
   *
   * 페이지를 열 때 세션 조회가 일시 실패하면 CSRF 없이 전송기가 만들어진다. 이 확인이
   * 없으면 네트워크가 돌아와도 새로고침 전까지 모든 쓰기가 `session_missing`에 머문다.
   */
  readonly adoptSession?: (server: SessionState) => boolean;
  readonly stageHashes: Readonly<Record<string, string>>;
  readonly now: () => number;
  /** 브라우저가 온라인이라고 보고하는가. 모르면 온라인으로 본다. */
  readonly online?: () => boolean;
  /** 이 페이지 로드의 clock segment. 시간 순서를 다시 쓰지 않았음을 남긴다. */
  readonly segment?: ClockSegment;
}

export interface Sender {
  /** batch 하나를 보낸다. */
  flushOnce(options?: FlushOptions): Promise<FlushOutcome>;
  /** 보낼 것이 없어질 때까지 보낸다. 실패하면 그 자리에서 멈춘다. */
  flushAll(options?: FlushOptions): Promise<FlushOutcome>;
  /** 시각·이동 수 조건이 찼을 때만 보낸다(10초 / 유효 이동 50회 / 강제). */
  tick(force?: boolean): Promise<FlushOutcome>;
  status(): SenderStatus;
  /** 서버가 아는 수신 범위로 로컬 표식을 맞춘다. */
  resync(): Promise<SenderStatus>;
  /**
   * 쓰기 임차를 잡거나 **갱신한다** (REV-002).
   *
   * 전송과 분리되어 있다. 예전에는 flush가 due일 때만 `acquire()`가 불려서, 10초
   * 간격과 8초 TTL 사이에서 임차가 조용히 만료됐고 두 탭이 같은 세션을 함께
   * 썼다. 화면은 이 값으로 자기가 쓰는 탭인지 판단한다.
   */
  hold(): boolean;
  /** 쓰기 임차를 놓아 준다. 창을 닫을 때 다음 탭이 이어받게 한다. */
  release(): void;
  /**
   * `release()` 뒤에 다시 쓸 수 있게 한다 (REV-002).
   *
   * 놓아 준 전송기는 스스로 다시 가져가지 않는다. 그러지 않으면 떠나는 탭이
   * 놓자마자 같은 호출 안에서 도로 가져가, 남는 탭이 보기 전용에 갇힌다.
   */
  resume(): void;
}

function toClientIntegrity(integrity: TelemetryIntegrity, spool: SpoolSnapshot): ClientIntegrity {
  return {
    complete: integrity.complete,
    dropped: integrity.dropped,
    rejected: integrity.rejected,
    invalid: integrity.invalid,
    sequence_gaps: integrity.sequenceGaps,
    sequence_conflicts: integrity.sequenceConflicts,
    unsequenced: integrity.unsequenced,
    // 확인 기록 필드는 묶음이다. 이벤트 무결성(`complete`)과 의미를 섞지 않는다.
    confirmations_dropped: spool.dropped,
    confirmations_state: spool.state,
  };
}

/** `stage_replay` 이벤트에서 끝난 시도의 경로 조각을 복원한다. */
function chunksFromEvents(events: readonly TelemetryEvent[]): PathChunk[] {
  const chunks: PathChunk[] = [];
  for (const event of events) {
    if (event.name !== 'stage_replay') {
      continue;
    }
    const attemptId = event.payload['attempt_id'];
    const path = event.payload['path'];
    const deltas = event.payload['deltas_ms'];
    if (typeof attemptId !== 'string' || typeof path !== 'string' || !Array.isArray(deltas)) {
      continue;
    }
    if (path.length === 0 || path.length !== deltas.length) {
      continue;
    }
    try {
      chunks.push(...toPathChunks(attemptId, path, deltas as readonly number[], {
        truncated: event.payload['truncated'] === true,
      }));
    } catch {
      // 모양이 맞지 않는 기록은 조각으로 만들지 않는다. 최종 제출이 누락으로 드러낸다.
    }
  }
  return chunks;
}

/**
 * 진행 중 시도의 **완결된** 조각만 만든다.
 *
 * 마지막 부분 조각은 보내지 않는다. 다음 수가 그 조각의 내용을 바꾸는데, 같은
 * `(attempt_id, index)`에 다른 내용을 보내면 서버가 409로 거절한다(경로 조각은
 * 한 번 저장되면 바뀌지 않는다). 부분 조각은 시도가 끝날 때 `stage_replay`에서
 * 완성된 모습으로 나간다.
 */
function chunksFromCheckpoint(state: AttemptState | undefined): PathChunk[] {
  if (state === undefined || state.counters.path.length < MAX_CHUNK_MOVES) {
    return [];
  }
  try {
    return chunksOf(state).filter((chunk) => chunk.path.length === MAX_CHUNK_MOVES);
  } catch {
    return [];
  }
}

/** 두 보고 중 더 나쁜 것. 한쪽이 없으면 다른 쪽이다. */
function worseOf(a: ConfirmationIntegrity | null, b: ConfirmationIntegrity | null): ConfirmationIntegrity | null {
  if (a === null) {
    return b;
  }
  if (b === null) {
    return a;
  }
  return integrityWorse(a, b) ? a : b;
}

export function createSender(deps: SenderDeps): Sender {
  const online = deps.online ?? (() => true);
  let sending = false;
  let lastFlushAt = 0;
  let movesAtLastFlush = 0;
  let nextAttemptAt = 0;
  /**
   * 이 전송기가 쓰기 임차를 스스로 놓았는가 (REV-002).
   *
   * 놓은 뒤에는 `resume()` 전까지 어떤 경로도 다시 가져가지 않는다. 페이지를
   * 떠나며 놓자마자 마지막 flush가 도로 가져가던 경로를 없앤다.
   */
  let released = false;

  let state = readQueueState(deps.storage, deps.sessionRef);
  if (deps.segment !== undefined && !state.segments.some((segment) => segment.id === deps.segment?.id)) {
    state = appendSegment(state, deps.segment);
  }
  let stateWritten = writeQueueState(deps.storage, state);
  /*
   * 새로고침으로 되살린 inflight가 확인 기록을 싣고 있으면 다시 pin한다 (ADR-002 §6).
   * 재전송하는 동안 그 기록이 상한 때문에 밀려나지 않게 한다.
   */
  if (state.inflight !== null && state.inflight.confirmation_ids.length > 0) {
    void deps.confirmations.pin(state.inflight.confirmation_ids);
  }

  /**
   * 이 세션의 기록 (R2-REV-001).
   *
   * `deps.log`는 세션 키 공간의 로그다(`app/recordingScope.ts`). 연습·다른 세션 탭의
   * 기록은 처음부터 이 로그에 없으므로 고르거나 다시 매길 것이 없다.
   */
  function sessionEvents(): readonly TelemetryEvent[] {
    const events = deps.log.readAll();
    const boundary = state.sealed_through;
    /*
     * 봉인 뒤에는 완료 경계까지만 이 세션의 기록이다 (CR-03). 그 뒤의 순번은 서버가 완료를
     * 확인한 다음 이 기기에만 생긴 것이라 보낼 수도, 미전송으로 셀 수도 없다. 지우지는 않는다.
     */
    return boundary === null ? events : events.filter((event) => (event.seq ?? 0) <= boundary);
  }

  function sealed(): boolean {
    return state.sealed_through !== null;
  }

  function save(next: QueueStateShape): void {
    state = next;
    stateWritten = writeQueueState(deps.storage, next);
  }

  function skipKeys(): Set<string> {
    return new Set([...state.acked_chunks, ...state.conflicted_chunks]);
  }

  function pendingChunks(): PathChunk[] {
    if (sealed()) {
      // 완료된 세션은 경로 조각도 더 받지 않는다. 완료 전 조각은 이미 ACK되었다.
      return [];
    }
    const skip = skipKeys();
    const seen = new Set<string>();
    const all = [...chunksFromEvents(sessionEvents()), ...chunksFromCheckpoint(deps.checkpoint())];
    const pending: PathChunk[] = [];
    for (const chunk of all) {
      const key = chunkKey(chunk.attemptId, chunk.index);
      if (skip.has(key) || seen.has(key)) {
        continue;
      }
      seen.add(key);
      pending.push(chunk);
    }
    return pending;
  }

  function localMaxSeq(): number {
    let max = 0;
    for (const event of sessionEvents()) {
      if (typeof event.seq === 'number' && event.seq > max) {
        max = event.seq;
      }
    }
    return max;
  }

  function pendingEventCount(): number {
    return sessionEvents().filter((event) => (event.seq ?? 0) > state.through_seq).length;
  }

  function currentMoves(): number {
    return deps.checkpoint()?.counters.moves ?? 0;
  }

  /** 아직 ACK를 받지 못해 버퍼에 남은 확인 기록 수. inflight에 실린 것도 여기 있다. */
  /** 아직 서버 ACK를 받지 못한 확인 기록 수. spool과 아직 spool에 넣지 못한 기록을 합한다. */
  function pendingConfirmations(): number {
    return sealed() ? 0 : deps.confirmations.snapshot().pending;
  }

  /**
   * 서버에 아직 보고하지 않은 확인 기록 손실·확인 불가가 있는가 (R4-REV-001).
   *
   * spool 상태가 마지막으로 ACK된 보고보다 나쁘면 참이다. 이 보고가 ACK되기 전에는 보낼 것이
   * 남은 것이므로 `synced`가 아니다.
   */
  function integrityPending(): boolean {
    if (sealed()) {
      return false;
    }
    const snapshot = deps.confirmations.snapshot();
    return snapshot.state !== 'complete' && integrityWorse(
      { confirmations_dropped: snapshot.dropped, confirmations_state: snapshot.state },
      state.last_acked_integrity,
    );
  }

  function statusState(): SenderState {
    if (sending) {
      return 'sending';
    }
    const pending =
      pendingEventCount() +
      pendingChunks().length +
      pendingConfirmations() +
      (integrityPending() ? 1 : 0) +
      (state.inflight === null ? 0 : 1);
    /*
     * 막혔다고 말하는 것은 **보낼 것이 남아 있을 때**뿐이다. 세션이 완료된 뒤의
     * `session_completed`처럼, 더 보낼 것이 없는데 남아 있는 마지막 오류를 "막혔다"로
     * 표시하면 저장이 끝난 화면이 실패처럼 보인다. 오류 자체는 `lastError`로 계속 보인다.
     */
    if (pending > 0 && state.last_error !== null && isBlockingCode(state.last_error)) {
      return 'blocked';
    }
    /*
     * 다른 탭이 쓰고 있으면 **보낼 것이 없어도** 보기 전용이다 (REV-002). 이 탭의
     * 대기열이 비었다는 사실이 이 탭에서 이어 해도 된다는 뜻은 아니다.
     */
    if (deps.lease.heldByOther()) {
      return 'readonly';
    }
    if (pending === 0) {
      return 'idle';
    }
    if (!online()) {
      return 'offline';
    }
    return 'pending';
  }

  function status(): SenderStatus {
    const max = localMaxSeq();
    const spool = deps.confirmations.snapshot();
    return {
      state: statusState(),
      pendingEvents: pendingEventCount(),
      pendingChunks: pendingChunks().length,
      throughSeq: state.through_seq,
      localMaxSeq: max,
      conflicts: state.conflicted_chunks.length,
      pendingConfirmations: pendingConfirmations(),
      droppedConfirmations: spool.dropped,
      confirmationsState: spool.state,
      confirmationsReason: spool.reason,
      lastError: stateWritten ? state.last_error : (state.last_error ?? 'queue_state_unsaved'),
      lastAckAt: state.last_ack_at,
      tries: state.inflight?.tries ?? 0,
      holder: deps.lease.held(),
      sealed: sealed(),
      synced:
        state.through_seq >= max &&
        state.inflight === null &&
        pendingChunks().length === 0 &&
        pendingConfirmations() === 0 &&
        !integrityPending(),
    };
  }

  function backoff(tries: number): number {
    return Math.min(BACKOFF_MAX_MS, BACKOFF_BASE_MS * 2 ** Math.max(0, tries - 1));
  }

  async function sendInflight(allowAdopt = true): Promise<FlushOutcome> {
    const inflight = state.inflight;
    if (inflight === null) {
      return { kind: 'idle' };
    }
    try {
      const ack = await deps.client.sendBatch(inflight.text);
      const at = deps.now();
      /*
       * **ACK 뒤에 지운다** (REV-005). 계획 단계에서 꺼내 버리면 크기 때문에 잘린
       * 나머지와 재시도·새로고침·영구 실패의 기록이 갈 곳을 잃는다. 확인 기록은
       * 버퍼 앞에서부터 순서대로 실렸으므로, 실린 수만큼 앞에서 빼면 정확히 같은
       * 부분집합이 지워진다. 그 사이에 새로 들어온 기록은 뒤에 붙어 그대로 남는다.
       */
      if (inflight.confirmation_ids.length > 0) {
        // ACK를 받은 그 기록만 spool에서 지우고 pin을 푼다. 누적 손실 수는 줄지 않는다.
        await deps.confirmations.commit(inflight.confirmation_ids);
      }
      save({
        ...state,
        // ACK는 서버가 연속 수신한 곳까지다. 로컬이 앞서 있으면 앞선 값을 지키지 않는다.
        through_seq: Math.max(state.through_seq, ack.through_seq),
        acked_chunks: [...state.acked_chunks, ...inflight.chunk_keys],
        inflight: null,
        // 이 batch가 보고한 확인 기록 무결성을 보고 완료로 남긴다. 더 나쁜 쪽을 지킨다.
        last_acked_integrity: worseOf(inflight.integrity, state.last_acked_integrity),
        last_error: null,
        last_error_at: null,
        last_ack_at: at,
      });
      return { kind: 'sent', batchId: ack.batch_id, throughSeq: state.through_seq, duplicate: ack.duplicate };
    } catch (error) {
      const api = error instanceof ApiError ? error : new ApiError(0, 'network_error');
      if (api.code === 'session_missing' && allowAdopt && deps.adoptSession !== undefined) {
        /*
         * 쓰기 토큰이 없어 요청을 만들지 못했다 (R2-REV-003). 서버에서 같은 참여임을 확인해
         * 토큰을 받은 뒤 같은 batch를 한 번 더 보낸다. 확인에 실패하면 그 사유가 남는다.
         */
        if (await adopt()) {
          return sendInflight(false);
        }
        if (state.last_error !== null && isBlockingCode(state.last_error)) {
          return { kind: 'blocked', code: state.last_error };
        }
        const wait = backoff(inflight.tries + 1);
        save({ ...state, inflight: { ...inflight, tries: inflight.tries + 1 } });
        nextAttemptAt = deps.now() + wait;
        return { kind: 'retry', code: state.last_error ?? api.code, retryAfterMs: wait };
      }
      const at = deps.now();
      if (api.retriable) {
        const tries = inflight.tries + 1;
        save({
          ...state,
          inflight: { ...inflight, tries },
          last_error: api.code,
          last_error_at: at,
        });
        const wait = backoff(tries);
        nextAttemptAt = at + wait;
        return { kind: 'retry', code: api.code, retryAfterMs: wait };
      }
      return handlePermanent(api, inflight);
    }
  }

  function handlePermanent(api: ApiError, inflight: NonNullable<QueueStateShape['inflight']>): FlushOutcome {
    const at = deps.now();
    /*
     * 같은 batch를 다시 보내도 같은 답이 오는 실패다. 무엇이 문제인지 남기고 이
     * batch는 더 돌리지 않는다. 자료 자체는 로그에 남아 있으므로, 순번 충돌처럼
     * 서버가 이미 받은 경우에는 resync가 표식을 앞으로 옮긴다.
     */
    const conflicted = api.code === 'path_chunk_conflict' ? inflight.chunk_keys : [];
    // 실린 확인 기록은 spool에 그대로 남아 있다. pin만 풀어 다음 batch가 다시 보게 한다.
    if (inflight.confirmation_ids.length > 0) {
      void deps.confirmations.pin([]);
    }
    save({
      ...state,
      inflight: null,
      conflicted_chunks: [...state.conflicted_chunks, ...conflicted],
      last_error: api.code,
      last_error_at: at,
    });
    return { kind: 'blocked', code: api.code };
  }

  function isBlockingCode(code: string): boolean {
    return [
      'session_required',
      'session_expired',
      'session_withdrawn',
      'session_completed',
      'campaign_closed',
      'session_event_limit',
      'session_mismatch',
      'origin_rejected',
      'csrf_rejected',
      'invalid_batch',
      'invalid_event',
      'invalid_confirmation',
      'unsupported_schema',
    ].includes(code);
  }

  /**
   * 서버 세션을 다시 읽어 표식을 맞춘다. 같은 참여로 확인되면 쓰기 토큰도 받아들인다.
   * 확인·토큰 반영까지 끝났으면 `true`.
   */
  async function adopt(): Promise<boolean> {
    try {
      const server = await deps.client.session();
      if (deps.adoptSession !== undefined && !deps.adoptSession(server)) {
        save({ ...state, last_error: 'session_mismatch', last_error_at: deps.now() });
        return false;
      }
      if (server.status === 'completed') {
        /*
         * 서버가 같은 참여의 완료를 확인했다 (CR-03). 그 순번을 완료 경계로 봉인한다. 보내던
         * batch와 남은 오류도 함께 정리한다 —— 완료된 세션에는 더 보낼 것이 없고, 그 오류는
         * 완료 뒤 꼬리를 보내려다 생긴 것이다. 이미 봉인했으면 경계를 바꾸지 않는다.
         */
        save({
          ...state,
          through_seq: Math.max(state.through_seq, server.through_seq),
          sealed_through: state.sealed_through ?? server.through_seq,
          inflight: null,
          last_error: null,
          last_error_at: null,
        });
        return false;
      }
      save({
        ...state,
        through_seq: Math.max(state.through_seq, server.through_seq),
        last_error: server.event_limit_reached ? 'session_event_limit' : null,
        last_error_at: server.event_limit_reached ? deps.now() : null,
      });
      return !server.event_limit_reached;
    } catch (error) {
      const api = error instanceof ApiError ? error : new ApiError(0, 'network_error');
      save({ ...state, last_error: api.code, last_error_at: deps.now() });
      return false;
    }
  }

  async function resync(): Promise<SenderStatus> {
    await adopt();
    return status();
  }

  async function flushOnce(options: FlushOptions = {}): Promise<FlushOutcome> {
    if (sealed()) {
      // 봉인된 세션은 보낼 것이 없다. 임차도 잡지 않고 요청도 만들지 않는다 (CR-03).
      return { kind: 'idle' };
    }
    if (sending) {
      return { kind: 'retry', code: 'busy', retryAfterMs: BACKOFF_BASE_MS };
    }
    if (options.manual !== true && !online()) {
      return { kind: 'offline' };
    }
    if (released || !deps.lease.acquire()) {
      return { kind: 'not_holder' };
    }
    if (
      state.last_error !== null &&
      isBlockingCode(state.last_error) &&
      // 다른 참여로 확인된 뒤에는 보내던 batch가 있어도 멈춘다. 그 batch의 대상이 불확실하다.
      (state.inflight === null || state.last_error === 'session_mismatch')
    ) {
      return { kind: 'blocked', code: state.last_error };
    }

    sending = true;
    try {
      if (state.inflight === null) {
        // 받아 둔 확인 기록을 먼저 spool에 넣고, 가장 오래된 것부터 본다(지우지 않는다).
        await deps.confirmations.drain();
        const peeked = await deps.confirmations.peek(MAX_BATCH_CONFIRMATIONS);
        const plan = planBatch({
          events: sessionEvents(),
          throughSeq: state.through_seq,
          chunks: pendingChunks(),
          ackedChunkKeys: skipKeys(),
          // 버퍼를 비우지 않고 본다. 실제로 실린 수만 inflight에 적고 ACK 뒤에 뺀다.
          confirmations: peeked.map((item) => item.record),
          integrity: toClientIntegrity(deps.log.integrity(), deps.confirmations.snapshot()),
          lastAckedIntegrity: state.last_acked_integrity,
          stageHashes: deps.stageHashes,
        });
        if (plan.kind === 'empty') {
          lastFlushAt = deps.now();
          movesAtLastFlush = currentMoves();
          return { kind: 'idle' };
        }
        if (plan.kind === 'oversize') {
          save({ ...state, last_error: 'batch_oversize', last_error_at: deps.now() });
          return { kind: 'blocked', code: 'batch_oversize' };
        }
        // 크기 때문에 줄었다면 앞에서부터 실린 만큼이다(outbox가 뒤를 잘라 낸다).
        const confirmationIds = peeked.slice(0, plan.batch.confirmationCount).map((item) => item.id);
        save({
          ...state,
          inflight: {
            batch_id: plan.batch.batchId,
            text: plan.batch.text,
            chunk_keys: plan.batch.chunkKeys,
            last_seq: plan.batch.lastSeq ?? null,
            tries: 0,
            confirmation_ids: confirmationIds,
            integrity: plan.batch.integrity,
          },
        });
        // inflight를 적은 뒤 pin한다. 보내는 동안 상한 때문에 이 기록이 밀려나지 않는다.
        if (confirmationIds.length > 0) {
          await deps.confirmations.pin(confirmationIds);
        }
      }
      const outcome = await sendInflight();
      lastFlushAt = deps.now();
      movesAtLastFlush = currentMoves();
      return outcome;
    } finally {
      sending = false;
    }
  }

  async function flushAll(options: FlushOptions = {}): Promise<FlushOutcome> {
    let last: FlushOutcome = { kind: 'idle' };
    for (let index = 0; index < MAX_BATCHES_PER_FLUSH; index += 1) {
      last = await flushOnce(options);
      if (last.kind !== 'sent') {
        return last;
      }
    }
    return last;
  }

  async function tick(force = false): Promise<FlushOutcome> {
    const at = deps.now();
    if (!force) {
      if (at < nextAttemptAt) {
        return { kind: 'retry', code: state.last_error ?? 'backoff', retryAfterMs: nextAttemptAt - at };
      }
      const dueByTime = at - lastFlushAt >= FLUSH_INTERVAL_MS;
      const dueByMoves = currentMoves() - movesAtLastFlush >= FLUSH_MOVE_COUNT;
      const dueByConfirmations = pendingConfirmations() >= FLUSH_CONFIRMATION_COUNT;
      if (!dueByTime && !dueByMoves && !dueByConfirmations) {
        return { kind: 'idle' };
      }
    }
    return flushAll();
  }

  return {
    flushOnce,
    flushAll,
    tick,
    status,
    resync,
    hold: () => {
      if (released) {
        return false;
      }
      return deps.lease.acquire();
    },
    release: () => {
      released = true;
      deps.lease.release();
    },
    resume: () => {
      released = false;
    },
  };
}
